import React, { Fragment, useEffect, useRef, useState } from "react";

import { Dialog, Transition } from "@headlessui/react";
import { LifeBuoy, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  CredentialAddedIllustration,
  OpenInWalletIllustration,
  ReviewOfferIllustration,
  TapAddButtonIllustration,
} from "./WalletIllustrations";

const SWIPE_CLOSE_THRESHOLD_PX = 96;
const SCROLL_SETTLE_MS = 120;

const SAFE_AREA_FOOTER = {
  paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))",
};

const HIDE_SCROLLBAR = { scrollbarWidth: "none" };

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const SECONDARY_BUTTON_CLASS =
  "inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl border-[1px] border-gray-200 bg-white px-[1rem] text-base font-semibold text-cfHeadingText transition-all hover:bg-gray-50 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2";

const PRIMARY_BUTTON_CLASS =
  "inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl bg-cfPrimary px-[1.25rem] text-base font-semibold text-cfSecondary shadow-[0_8px_20px_-8px_var(--cf-primary-color)] transition-all hover:bg-cfPrimaryDark active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2";

const WalletHelpSheet = ({
  isOpen,
  onClose,
  credentialName,
  issuerName,
  issuerLogoUrl,
}) => {
  const { t } = useTranslation(["walletOffer"]);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const dragStartY = useRef(null);
  const trackRef = useRef(null);
  const scrollSettleTimer = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDragOffset(0);
      setActiveIndex(0);
    }
  }, [isOpen]);

  useEffect(() => () => clearTimeout(scrollSettleTimer.current), []);

  const handlePointerDown = (event) => {
    dragStartY.current = event.clientY;
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (dragStartY.current === null) return;
    setDragOffset(Math.max(0, event.clientY - dragStartY.current));
  };

  const handlePointerEnd = () => {
    if (dragStartY.current === null) return;
    dragStartY.current = null;
    setIsDragging(false);
    if (dragOffset > SWIPE_CLOSE_THRESHOLD_PX) {
      setDragOffset(window.innerHeight);
      onClose();
    } else {
      setDragOffset(0);
    }
  };

  const illustrationProps = {
    credentialName,
    issuerName,
    logoUrl: issuerLogoUrl,
    buttonLabel: t("walletOffer:add_to_wallet"),
  };

  const steps = [
    {
      key: "tap",
      illustration: (
        <TapAddButtonIllustration
          {...illustrationProps}
          hint={t("walletOffer:hint_tap_here")}
        />
      ),
    },
    {
      key: "open",
      illustration: (
        <OpenInWalletIllustration
          {...illustrationProps}
          hint={t("walletOffer:help_hint_tap_open")}
        />
      ),
    },
    {
      key: "review",
      illustration: (
        <ReviewOfferIllustration
          {...illustrationProps}
          hint={t("walletOffer:help_hint_confirm")}
        />
      ),
    },
    {
      key: "done",
      illustration: (
        <CredentialAddedIllustration
          credentialName={credentialName}
          logoUrl={issuerLogoUrl}
        />
      ),
    },
  ];

  const lastIndex = steps.length - 1;
  const isLastStep = activeIndex === lastIndex;

  const goToStep = (index) => {
    const track = trackRef.current;
    const nextIndex = Math.min(Math.max(index, 0), lastIndex);
    setActiveIndex(nextIndex);
    track?.scrollTo?.({
      left: nextIndex * track.clientWidth,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };

  const handleTrackScroll = () => {
    clearTimeout(scrollSettleTimer.current);
    scrollSettleTimer.current = setTimeout(() => {
      const track = trackRef.current;
      if (!track?.clientWidth) return;
      setActiveIndex(Math.round(track.scrollLeft / track.clientWidth));
    }, SCROLL_SETTLE_MS);
  };

  const panelStyle = dragOffset
    ? {
        transform: `translateY(${dragOffset}px)`,
        transition: isDragging ? "none" : undefined,
      }
    : undefined;

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/45 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-x-0 bottom-0 flex justify-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="translate-y-full"
            enterTo="translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="translate-y-0"
            leaveTo="translate-y-full"
          >
            <Dialog.Panel
              style={panelStyle}
              className="relative flex max-h-[92vh] w-full supports-[height:100dvh]:max-h-[92dvh] max-w-lg transform flex-col rounded-t-3xl bg-white font-CfPrimaryFont shadow-[0_-18px_50px_-20px_rgba(16,24,40,0.35)] transition-transform duration-200"
            >
              <div
                className="shrink-0 touch-none px-[1.25rem] pt-[0.75rem]"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerEnd}
                onPointerCancel={handlePointerEnd}
              >
                <span
                  aria-hidden="true"
                  className="mx-auto block h-1 w-10 rounded-full bg-gray-300"
                />
                <div className="mt-[1rem] flex items-start gap-3 pr-10">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-cfTableHeaderBackgroundColor text-cfPrimary">
                    <LifeBuoy className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <Dialog.Title
                      as="h2"
                      className="mb-0 text-lg font-bold leading-snug text-cfHeadingText"
                    >
                      {t("walletOffer:help_title")}
                    </Dialog.Title>
                    <Dialog.Description
                      as="p"
                      className="mt-0.5 text-sm text-gray-500"
                    >
                      {t("walletOffer:help_subtitle")}
                    </Dialog.Description>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                aria-label={t("walletOffer:help_close")}
                className="absolute right-[1rem] top-[1.5rem] flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-cfHeadingText focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>

              <div className="flex-1 overflow-y-auto overscroll-contain pt-[1rem]">
                <div
                  ref={trackRef}
                  onScroll={handleTrackScroll}
                  style={HIDE_SCROLLBAR}
                  aria-roledescription="carousel"
                  aria-label={t("walletOffer:help_title")}
                  className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain [&::-webkit-scrollbar]:hidden"
                >
                  {steps.map(({ key, illustration }, index) => (
                    <section
                      key={key}
                      role="group"
                      aria-roledescription="slide"
                      aria-label={t("walletOffer:help_step_counter", {
                        current: index + 1,
                        total: steps.length,
                      })}
                      aria-hidden={index !== activeIndex}
                      className="w-full shrink-0 snap-center px-[1.25rem]"
                    >
                      <div className="flex justify-center overflow-hidden rounded-2xl bg-gray-50 px-6 py-[1.25rem] ring-1 ring-gray-100">
                        <div className="h-[272px] [@media(max-height:700px)]:h-[204px]">
                          <div className="origin-top scale-[0.8] [@media(max-height:700px)]:scale-[0.6]">
                            {illustration}
                          </div>
                        </div>
                      </div>
                      <div className="mt-[1rem] flex items-start gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-cfPrimary text-sm font-semibold text-cfSecondary shadow-[0_6px_14px_-6px_var(--cf-primary-color)]">
                          {index + 1}
                        </span>
                        <div className="min-w-0">
                          <h3 className="mb-0 text-base font-semibold text-cfHeadingText">
                            {t(`walletOffer:help_step_${key}_title`)}
                          </h3>
                          <p className="mt-0.5 text-sm leading-relaxed text-gray-500">
                            {t(`walletOffer:help_step_${key}_description`, {
                              issuerName:
                                issuerName ||
                                t("walletOffer:help_issuer_fallback"),
                            })}
                          </p>
                        </div>
                      </div>
                    </section>
                  ))}
                </div>

                <div className="mt-[1rem] flex items-center justify-center gap-1.5">
                  {steps.map(({ key }, index) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => goToStep(index)}
                      aria-label={t("walletOffer:help_go_to_step", {
                        step: index + 1,
                      })}
                      aria-current={index === activeIndex ? "step" : undefined}
                      className={`h-2 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2 ${
                        index === activeIndex
                          ? "w-6 bg-cfPrimary"
                          : "w-2 bg-gray-300 hover:bg-gray-400"
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div
                className="flex shrink-0 gap-3 px-[1.25rem] pt-[1rem]"
                style={SAFE_AREA_FOOTER}
              >
                {activeIndex > 0 ? (
                  <button
                    type="button"
                    onClick={() => goToStep(activeIndex - 1)}
                    className={SECONDARY_BUTTON_CLASS}
                  >
                    {t("walletOffer:help_back")}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={
                    isLastStep ? onClose : () => goToStep(activeIndex + 1)
                  }
                  className={PRIMARY_BUTTON_CLASS}
                >
                  {isLastStep
                    ? t("walletOffer:help_got_it")
                    : t("walletOffer:help_next")}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default WalletHelpSheet;
